import { mapWordToRefText } from '../../../learn/mappers/word-to-reftext.mapper.ts';

import { LessonTaskStepKeys } from '../../constants/lesson-task-step-keys.ts';
import { StepType, TaskType } from '../../types/asset/asset.enums.ts';
import { AssetsFile, BaseLoaderUtil } from '../../types/asset/asset.types.ts';
import {
  Lesson,
  LessonKey,
  LessonStep,
  LessonTask,
  LessonTaskPictureExtension,
  LessonTaskKey,
  LessonTaskStepKey,
  LessonTaskStepWithLessonAndTask,
  LessonTaskWithLesson,
} from '../../types/asset/lesson.types.ts';
import { PreloadableAsset, PreloadableAssetType, PreloadableAssetGroup } from '../../types/asset/preloading.types.ts';
import { KeyedWord } from '../../types/asset/word.types.ts';

import { AssetFilesUtil, getAssetFilesUtil } from './asset-files.util.ts';
import { CharactersUtil, getCharactersUtil } from './characters.util.ts';
import { getLettersUtil, LettersUtil } from './letters.util.ts';
import { getWordsUtil, WordsUtil } from './words.util.ts';

export class LessonsUtil implements BaseLoaderUtil {
  private static _instance: LessonsUtil;
  private lessons: Lesson[] = [];

  private constructor(
    private readonly assetFilesUtil: AssetFilesUtil = getAssetFilesUtil(),
    private readonly lettersUtil: LettersUtil = getLettersUtil(),
    private readonly wordsUtil: WordsUtil = getWordsUtil(),
    private readonly charactersUtil: CharactersUtil = getCharactersUtil()
  ) {
    // Private constructor to enforce singleton pattern
  }

  public static get instance(): LessonsUtil {
    if (!LessonsUtil._instance) {
      LessonsUtil._instance = new LessonsUtil();
    }
    return LessonsUtil._instance;
  }

  public load(assets: AssetsFile): void {
    this.lessons = Object.values(assets.lessons);
    console.log(`Loaded ${this.lessons.length} lessons`);
  }

  public getLessons(): Lesson[] {
    return this.lessons;
  }

  public getLesson(index: LessonKey): Lesson | undefined {
    return this.lessons[index.lesson];
  }

  public getLessonTask(index: LessonTaskKey): LessonTaskWithLesson | undefined {
    if (index.lesson === -1 || index.task === -1) {
      return undefined;
    }

    const lesson = this.lessons[index.lesson];
    const task = lesson?.tasks[index.task];
    return { lesson, task };
  }

  public getLessonTaskStep(index: LessonTaskStepKey): LessonTaskStepWithLessonAndTask | undefined {
    if (index.lesson === -1 || index.task === -1 || index.step === -1) {
      return undefined;
    }

    const lesson = this.lessons[index.lesson];
    const task = lesson?.tasks[index.task];
    const step = task?.steps[index.step];
    return { lesson, task, step };
  }

  public getPreviousLessonTaskKey(index: LessonTaskStepKey): LessonTaskStepKey {
    const lesson = this.getLesson(index);
    if (!lesson) return LessonTaskStepKeys.DEFAULT_CURRENT;
    if (index.task === 0) {
      if (index.lesson === 0) return LessonTaskStepKeys.DEFAULT_CURRENT;
      // Previous lesson
      const previousLesson = this.getLesson({ lesson: index.lesson - 1 });
      if (!previousLesson) return LessonTaskStepKeys.DEFAULT_CURRENT;
      return {
        lesson: index.lesson - 1,
        task: previousLesson.tasks.length - 1,
        step: 0,
      };
    }
    // Previous task
    return { lesson: index.lesson, task: index.task - 1, step: 0 };
  }

  public getNextLessonTaskKey(index: LessonTaskStepKey): LessonTaskStepKey {
    const lesson = this.getLesson(index);
    if (!lesson) return LessonTaskStepKeys.DEFAULT_CURRENT;
    if (index.task === lesson.tasks.length - 1) {
      // Next lesson
      return { lesson: index.lesson + 1, task: 0, step: 0 };
    }
    // Next task
    return { lesson: index.lesson, task: index.task + 1, step: 0 };
  }

  public getPreviousStepKey(index: LessonTaskStepKey): LessonTaskStepKey {
    const lesson = this.getLesson(index);
    if (!lesson) return LessonTaskStepKeys.DEFAULT_CURRENT;
    if (index.step === 0) {
      if (index.task === 0) {
        if (index.lesson === 0) return LessonTaskStepKeys.DEFAULT_CURRENT;
        // Previous lesson
        const previousLesson = this.getLesson({ lesson: index.lesson - 1 });
        if (!previousLesson) return LessonTaskStepKeys.DEFAULT_CURRENT;
        return {
          lesson: index.lesson - 1,
          task: previousLesson.tasks.length - 1,
          step: previousLesson.tasks[previousLesson.tasks.length - 1].steps.length - 1,
        };
      }
      // Previous task
      const previousTask = this.getLessonTask({ lesson: index.lesson, task: index.task - 1 });
      if (!previousTask) return LessonTaskStepKeys.DEFAULT_CURRENT;
      return { lesson: index.lesson, task: index.task - 1, step: previousTask.task.steps.length - 1 };
    }
    // Previous step
    return { lesson: index.lesson, task: index.task, step: index.step - 1 };
  }

  public getNextStepKey(index: LessonTaskStepKey): LessonTaskStepKey {
    const data = this.getLessonTaskStep(index);
    if (!data) return LessonTaskStepKeys.DEFAULT_CURRENT;
    const { lesson, task } = data;
    if (!task || !lesson) return LessonTaskStepKeys.DEFAULT_CURRENT;

    if (index.step === task.steps.length - 1) {
      if (index.task === lesson.tasks.length - 1) {
        // Next lesson
        return { lesson: index.lesson + 1, task: 0, step: 0 };
      }
      // Next task
      return { lesson: index.lesson, task: index.task + 1, step: 0 };
    }
    // Next step
    return { lesson: index.lesson, task: index.task, step: index.step + 1 };
  }

  public getLessonByNumber(lessonNumber: number): Lesson | undefined {
    return this.lessons.find((lesson) => lesson.lessonTitle === `Lesson ${lessonNumber}`);
  }

  public getStoryImageUrl(picture: string, fileType = 'jpg'): string {
    return this.assetFilesUtil.getS3Url(`img/lessons/${picture}`, fileType);
  }

  public getStepParts(task: LessonTask, step: LessonStep): string[] | null {
    return step.parts ?? this.getStepWord(task, step)?.parts ?? null;
  }

  public getStepLetter(task: LessonTask, step: LessonStep): string | null {
    return task.letters?.[step.index ?? 0] ?? null;
  }

  public getStepLetterRefText(task: LessonTask, step: LessonStep): string | null {
    const key = this.getStepLetter(task, step);
    if (!key) return null;
    const letter = this.lettersUtil.getLetter(key);
    return letter?.pronunciations[letter.pronunciations.length - 1] ?? null;
  }

  public getStepLetterNameRefText(task: LessonTask, step: LessonStep): string | null {
    const key = this.getStepLetter(task, step);
    return key;
  }

  public getStepWord(task: LessonTask, step: LessonStep): KeyedWord | null {
    if (task.words && step.index !== undefined) {
      const key = task.words[step.index];
      const word = this.wordsUtil.getWord(key);
      return word ?? null;
    } else if (task.story && step.index !== undefined && step.subindex !== undefined) {
      const key = task.story[step.index][step.subindex];
      const word = this.wordsUtil.getWord(key);
      return word ?? null;
    }
    return null;
  }

  public getStepRhymingSoundPronunciation(task: LessonTask, step: LessonStep): string | null {
    if (task.rhymingSounds && step.index !== undefined) {
      const key = task.rhymingSounds[step.index];
      const word = this.wordsUtil.getWord(key);
      if (word) {
        return word.pronunciations[0];
      }
      const letter = this.lettersUtil.getLetter(key);
      return letter?.pronunciations[letter.pronunciations.length - 1] ?? null;
    }
    return null;
  }

  public getStepRhymingSoundRefText(task: LessonTask, step: LessonStep): string | null {
    if (task.rhymingSounds && step.index !== undefined) {
      const key = task.rhymingSounds[step.index];
      const word = this.wordsUtil.getWord(key);
      if (word) {
        return mapWordToRefText(word);
      }
      const letter = this.lettersUtil.getLetter(key);
      return letter?.pronunciations[letter.pronunciations.length - 1] ?? null;
    }
    return null;
  }

  public getNextStep(index: LessonTaskStepKey): LessonTaskStepWithLessonAndTask | null {
    const nextStepKey = this.getNextStepKey(index);
    if (!nextStepKey) return null;
    return this.getLessonTaskStep(nextStepKey) ?? null;
  }

  public getLessonWithNumber(lessonNumber: number): Lesson | null {
    return this.lessons[this.findIndexOfLessonWithNumber(lessonNumber)] ?? null;
  }

  public getPronunciation(key: string): string | null {
    const letter = this.lettersUtil.getLetter(key);

    if (letter && letter.pronunciations.length > 0) {
      return letter.pronunciations[0];
    }

    const word = this.wordsUtil.getWord(key);
    if (word && word.pronunciations.length > 0) {
      return word.pronunciations[0];
    }

    return null;
  }

  public getPronunciationKey(task: LessonTask, step: LessonStep): string | undefined {
    let key: string | undefined;
    switch (step.stepType) {
      case StepType.AppPronounce:
        key = task.letters?.[step.index ?? 0];
        break;
      case StepType.AppSoundOut:
      case StepType.AppSay:
      case StepType.AppSayFast:
        if (task.story) {
          key = task.story[step.index!][step.subindex!];
        } else {
          key = task.words?.[step.index ?? 0];
        }
        break;
    }

    return key;
  }

  public isFinalStep(index: LessonTaskStepKey): boolean {
    const data = this.getLessonTask(index);
    if (!data) {
      return false;
    }

    return index.step === data.task.steps.length - 1;
  }

  public hasLessonOnlyAllowedTaskTypes(lesson: Lesson, allowedTaskTypes: TaskType[]): boolean {
    return lesson.tasks.every((task) => allowedTaskTypes.includes(task.taskType));
  }

  public getLessonTaskPictureUrl(
    task: LessonTask,
    fileType: LessonTaskPictureExtension = LessonTaskPictureExtension.Jpg
  ): string | undefined {
    if (!task.picture) {
      return undefined;
    }

    return this.assetFilesUtil.getS3Url(`img/lessons/${task.picture}`, fileType);
  }

  private getLessonTaskPictureAssets(lesson: Lesson): PreloadableAsset[] {
    return lesson.tasks.flatMap((task) => {
      if (!task.picture) {
        return [];
      }

      return [
        {
          type: PreloadableAssetType.Image,
          key: `lesson-${lesson.lessonTitle}-task-${task.taskType}-picture`,
          url: this.getLessonTaskPictureUrl(task, LessonTaskPictureExtension.Jpg)!,
        },
      ];
    });
  }

  private getLessonLetterAssets(lesson: Lesson): PreloadableAsset[] {
    const assets = lesson.tasks.flatMap((task) => {
      if (!task.letters) {
        return [];
      }

      return task.letters.flatMap((letter) => this.lettersUtil.getSingleLetterAssets(letter));
    });
    const assetArray = assets.flat();
    const uniqueAssets = new Map<string, PreloadableAsset>(assetArray.map((asset) => [asset.url, asset]));
    return Array.from(uniqueAssets.values());
  }

  public getLessonAssets(lessonKey: LessonKey): PreloadableAssetGroup {
    const lesson = this.getLesson(lessonKey);
    if (!lesson) {
      return {
        criticalAssets: [],
        lazyAssets: [],
      };
    }

    return {
      criticalAssets: this.charactersUtil.getAssets(lesson.animalCharacterKey),
      lazyAssets: [...this.getLessonTaskPictureAssets(lesson), ...this.getLessonLetterAssets(lesson)],
    };
  }

  private findIndexOfLessonWithNumber(lessonNumber: number): number {
    return this.findIndexOfLessonWithTitle(`Lesson ${lessonNumber}`);
  }

  private findIndexOfLessonWithTitle(lessonTitle: string): number {
    return this.lessons.findIndex((lesson) => lesson.lessonTitle === lessonTitle);
  }
}

export const getLessonsUtil = (): LessonsUtil => LessonsUtil.instance;
